import React, { useState, useMemo } from 'react';
import { PageHeader, Input, Space, Table, Avatar, Tag } from 'antd';
import { CalendarOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import moment from 'moment';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { useCustomers } from '../../../models/useCustomers';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

import '../../../App.scss';
import 'antd/dist/antd.css';

const URLPath = getAppBaseUrl();
const LIMIT = 200;

export default function AdminCustomersScreen() {
  const [searchText, setSearchText] = useState('');
  const [loading, customers] = useCustomers();

  const filtered = useMemo(() => {
    const list = customers || [];
    const q = (searchText || '').toLowerCase().trim();
    if (!q) return list;
    return list.filter((c) => {
      const haystack = [c.id, c.userId, c.email, c.displayName, c.phoneNumber, c.authProvider, c.authEmail]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [customers, searchText]);

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, c) => {
        const href = `${URLPath}/admin/customers/${c.id}`;
        const label = c.displayName || c.email || c.authEmail || c.id;
        const createdLabel = c.createdAt && c.createdAt.toMillis
          ? moment(c.createdAt.toMillis()).format('YYYY-MM-DD HH:mm')
          : '-';
        const email = c.email || c.authEmail || null;
        const phone = c.phoneNumber || null;
        return (
          <Space direction="vertical" size={2}>
            <Space>
              <Avatar src={c.avatar || null}>{label.charAt(0).toUpperCase()}</Avatar>
              <a href={href}>{label}</a>
            </Space>
            {email ? (
              <Space size={6} style={{ fontSize: 12 }}>
                <MailOutlined />
                <span>{email}</span>
              </Space>
            ) : phone ? (
              <Space size={6} style={{ fontSize: 12 }}>
                <PhoneOutlined />
                <span>{phone}</span>
              </Space>
            ) : null}
            <Space size={6} style={{ fontSize: 12 }}>
              <CalendarOutlined />
              <span>{createdLabel}</span>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Auth',
      dataIndex: 'authProvider',
      key: 'authProvider',
      render: (v) => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: 'Customer ID',
      dataIndex: 'id',
      key: 'customerId',
      render: (id) => (
        <a href={`${URLPath}/admin/customers/${id}`}>
          <code style={{ fontSize: 11 }}>{id}</code>
        </a>
      ),
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (uid) =>
        uid ? (
          <a href={`${URLPath}/admin/payers/${uid}`}>
            <code style={{ fontSize: 11 }}>{uid}</code>
          </a>
        ) : '-',
    },
  ];

  return (
    <div style={{ height: '100%', textAlign: 'center' }}>
      <PageHeader
        ghost={false}
        title="Live Customers"
        subTitle={`Opted-in payers with customer role (max ${LIMIT})`}
      />

      <Space style={{ width: '100%', padding: '0 12px 12px 12px' }} direction="vertical">
        <Space wrap>
          <Input.Search
            placeholder="Search email, userId, displayName, phone..."
            allowClear
            style={{ minWidth: 340 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
          <span style={{ color: (filtered || []).length >= LIMIT ? '#ff4d4f' : undefined }}>
            {(filtered || []).length} result{(filtered || []).length !== 1 ? 's' : ''} (max {LIMIT})
          </span>
        </Space>
      </Space>

      {loading ? (
        <CenteredSpinner loading={true} noLayout={false} />
      ) : (
        <Table
          style={{ textAlign: 'left', padding: '0 12px 12px 12px' }}
          rowKey="id"
          dataSource={filtered}
          columns={columns}
          scroll={{ x: 800 }}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />
      )}
    </div>
  );
}
